<template>
    <div
        class="Question Question-wrapper"
        :class="{
            'Question--desktop': desktop,
            'active': active,
            'visible': visible
        }"
        @mousedown="nextQuestion"
    >
        <div class="Question-titleWrapper">
            <span class="Question-index">{{ index }}/{{ total }}</span>
            <span
                class="Question-label"
                v-html="question"/>
            <router-link :to="{ path: '/find', query: this.$route.query }">
                <hempel-link
                    v-if="help && desktop"
                    @click.native="handleAnalytics"
                    :label="translation.find.answers.help"
                    icon="antifoulingCanHelpBlue"
                />
            </router-link>
        </div>
        <div
            class="Question-answerWrapper"
            :class="{
                'Question--antifouling': antifouling,
                'Question--mobile': isMobile,
                'Question--dimensions': type === 'dimensions'
            }"
        >
            <hempel-answer
                v-if="!location && type !== 'dimensions'"
                v-for="(answer, index) in answers"
                :label="answer.label"
                :value="answer.value"
                :selected="value === answer.value"
                :sublabel="answer.sublabel"
                :icon="answer.icon"
                :image="answer.img"
                :desktop="desktop"
                :big="big"
                :antifouling="antifouling"
                :key="index"
                @input="answerSelected"
            />
            <hempel-input-dropdown
                v-if="location && type !== 'dimensions'"
                :value="value"
                :options="answers[0].countries"
                :desktop="!isMobile"
                @input="answerSelected"
            />
            <template v-if="type === 'dimensions'">
                <div class="dimensionsQuestion">
                    <div class="dimensionOptionGroup group-individual">
                        <div class="dimensionOptionLabel">{{ translation.calculate.answers.dimensionTitles[0].title }}</div>
                        <hempel-input-dropdown
                            :value="dimensionValue[0]"
                            :options="isUs ? answers[0].valuesUS : answers[0].values"
                            :desktop="!isMobile"
                            dimensions
                            :label="answers[0].label"
                            name="units"
                            :error="(formSubmitted && !(dimensionValue[0] || tempDimensions[0]))"
                            @input="dimensionSelected(0, $event.value)"
                        />
                        <hempel-input-text
                            :value="dimensionValue[1]"
                            :desktop="!isMobile"
                            :label="answers[1].label"
                            type="number"
                            placeholder="5"
                            name="waterline"
                            @input="dimensionSelected(1, $event.value)"
                        />
                        <hempel-input-text
                            :value="dimensionValue[2]"
                            :desktop="!isMobile"
                            :label="answers[2].label"
                            type="number"
                            placeholder="5"
                            name="beam"
                            @input="dimensionSelected(2, $event.value)"
                        />
                        <hempel-input-text
                            :value="dimensionValue[3]"
                            :desktop="!isMobile"
                            :label="answers[3].label"
                            type="number"
                            placeholder="5"
                            name="draft"
                            @input="dimensionSelected(3, $event.value)"
                        />
                    </div>
                    <div class="dimensionOptionGroup group-m2">
                        <div class="dimensionOptionLabel">{{ translation.calculate.answers.dimensionTitles[1].title }}</div>
                        <hempel-input-text
                            :value="dimensionValue[4]"
                            :desktop="!isMobile"
                            :label="answers[4].label"
                            type="number"
                            placeholder="25"
                            name="m2"
                            @input="dimensionSelected(4, $event.value)"
                        />
                    </div>
                </div>
            </template>
        </div>
        <div
            v-if="type === 'dimensions'"
            class="Button-wrapperDimensions"
            :class="{'Button-wrapperDimensionsMobile': isMobile}"
        >
            <button
                @click="dimensionSubmit"
                class="Button Button--primary Button--large">{{ translation.calculate.letsCalculate }}</button>
        </div>
        <div
            v-if="help && !desktop"
            class="Question-help">
            <router-link :to="{ path: '/find', query: this.$route.query }">
                <hempel-link
                    @click="antifoulingHelp"
                    :label="translation.find.answers.help"
                    icon="antifoulingCanHelpWhite"
                />
            </router-link>
        </div>
    </div>
</template>
<script>
import HempelAnswer from '@/components/shared/answer/Answer'
import HempelLink from '@/components/shared/link/Link'
import HempelInputDropdown from '@/components/shared/input/InputDropdown'
import HempelInputText from '@/components/shared/input/InputText'
import HempelButton from '@/components/shared/button/Button'

export default {
    name: 'HempelQuestion',
    data() {
        return {
            tempDimensions: [],
            formSubmitted: false
        }
    },
    props: {
        index: {
            type: Number,
            required: true
        },
        type: {
            type: String,
            required: true
        },
        value: {
            type: [Number, String]
        },
        total: {
            type: Number,
            required: true
        },
        question: {
            type: String,
            required: true
        },
        answers: {
            type: Array,
            required: true,
            default: () => []
        },
        desktop: {
            type: Boolean,
            default: false
        },
        big: {
            type: Boolean,
            default: false
        },
        antifouling: {
            type: Boolean,
            default: false
        },
        help: {
            type: Boolean,
            default: false
        },
        active: {
            type: Boolean,
            default: false
        },
        visible: {
            type: Boolean,
            default: false
        },
        location: {
            type: Boolean,
            default: false
        },
        dimensions: {
            type: Boolean,
            default: false
        }
    },
    components: {
        HempelAnswer,
        HempelLink,
        HempelInputDropdown,
        HempelButton,
        HempelInputText
    },
    watch: {
        visible() {
            this.checkAnswers()
        },
        '$route.query.paintLocation': function() {
            this.setInitialUnit()
        }
    },
    computed: {
        isUs() {
            return (
                this.$route.query.paintLocation === 'en-US' || this.translation.language === 'en-US'
            )
        },
        dimensionValue() {
            return this.value
                ? (this.value || this.$route.query.dimensions).split('-')
                : this.tempDimensions[0].unit === 'f'
                    ? this.tempDimensions.map((dimension, index) => {
                          if (index > 0) {
                              return dimension.valueF
                          }
                          return dimension.unit
                      })
                    : this.tempDimensions.map((dimension, index) => {
                          if (index > 0) {
                              return dimension.valueM
                          }
                          return dimension.unit
                      })
        }
    },
    methods: {
        dimensionSubmit() {
            this.formSubmitted = true
            const values = this.dimensionValue.filter(val => !!val).length
            if (values <= 5) {
                this.dataLayerAdd({
                    type: 'calculatorEvent',
                    category: 'Paint Calculator',
                    action: 'Calculate Volume'
                })
                this.$router.push({ path: `${this.$route.path}_results`, query: this.$route.query })
            }
        },
        dimensionSelected(index, value) {
            const tempValue = value.replace(',', '.')
            if (index === 0) {
                this.tempDimensions[0].unit = tempValue
            } else if (this.tempDimensions.length <= 5) {
                this.tempDimensions[0].unit === 'f'
                    ? (this.tempDimensions[index] = {
                          valueM: this.convertToMeters(tempValue),
                          valueF: tempValue
                      })
                    : (this.tempDimensions[index] = {
                          valueM: tempValue,
                          valueF: this.convertToFeet(tempValue)
                      })
            }
            const values = this.tempDimensions.filter(val => !!val).length
            if (values <= 5) {
                this.answerSelected({
                    value:
                        this.tempDimensions[0].unit === 'f'
                            ? this.tempDimensions
                                  .map(
                                      (dimension, index) =>
                                          index > 0 ? dimension.valueF : dimension.unit
                                  )
                                  .join('-')
                            : this.tempDimensions
                                  .map(
                                      (dimension, index) =>
                                          index > 0 ? dimension.valueM : dimension.unit
                                  )
                                  .join('-'),
                    replace: true
                })
            }
        },
        nextQuestion() {
            this.$emit('nextQuestion', this.index)
        },
        answerSelected(e) {
            this.$emit('change', {
                key: this.type,
                value: e.value,
                replace: e.replace
            })
        },
        checkAnswers(initial) {
            if (
                ((initial && this.active) || this.visible) &&
                (!this.answers || !this.answers.length)
            ) {
                this.$emit('error', {
                    key: this.type
                })
            }
        },
        antifoulingHelp() {
            this.handleAnalytics()
            this.$router.push({ path: '/find', query: this.$route.query })
        },
        setInitialUnit() {
            if (this.$route.query.dimensions && this.$route.query.dimensions.length > 0) {
                const unit = this.$route.query.dimensions.split('-')[0]
                this.$set(this.tempDimensions, 0, { unit })
            } else if (
                `${this.$route.query.paintLocation}` === 'en-GB' ||
                `${this.$route.query.paintLocation}` === 'en-US'
            ) {
                this.$set(this.tempDimensions, 0, { unit: 'f' })
            } else {
                this.$set(this.tempDimensions, 0, { unit: 'm' })
            }
        },
        convertToFeet(value) {
            return (value * 3.28084).toFixed(2)
        },
        convertToMeters(value) {
            return (value * 0.3048).toFixed(2)
        },
        doInitialCalculation() {
            if (this.$route.query.dimensions && this.$route.query.dimensions.length > 0) {
                const dimensions = this.$route.query.dimensions.split('-')
                if (dimensions[0] === 'f ') {
                    this.$set(this.tempDimensions, 1, {
                        valueM: this.convertToMeters(dimensions[1]),
                        valueF: dimensions[1]
                    })
                    this.$set(this.tempDimensions, 2, {
                        valueM: this.convertToMeters(dimensions[2]),
                        valueF: dimensions[2]
                    })
                    this.$set(this.tempDimensions, 3, {
                        valueM: this.convertToMeters(dimensions[3]),
                        valueF: dimensions[3]
                    })
                    this.$set(this.tempDimensions, 4, {
                        valueM: this.convertToMeters(dimensions[4]),
                        valueF: dimensions[4]
                    })
                } else {
                    this.$set(this.tempDimensions, 1, {
                        valueM: dimensions[1],
                        valueF: this.convertToFeet(dimensions[1])
                    })
                    this.$set(this.tempDimensions, 2, {
                        valueM: dimensions[2],
                        valueF: this.convertToFeet(dimensions[1])
                    })
                    this.$set(this.tempDimensions, 3, {
                        valueM: dimensions[3],
                        valueF: this.convertToFeet(dimensions[1])
                    })
                    this.$set(this.tempDimensions, 4, {
                        valueM: dimensions[4],
                        valueF: this.convertToFeet(dimensions[1])
                    })
                }
            }
        },
        handleAnalytics() {
            this.dataLayerAdd({ type: 'virtualPageview' }, '/find-product/start')
            this.dataLayerAdd({
                type: 'calculatorEvent',
                category: 'Paint Calculator',
                action: 'Misc',
                label: 'I need help with choosing antifouling'
            })
        }
    },
    beforeMount() {
        if (this.dimensions) {
            this.setInitialUnit()
            this.doInitialCalculation()
        }
    },
    mounted() {
        this.checkAnswers(true)
    }
}
</script>
